import { XMarkIcon } from "@heroicons/react/24/outline";
import { Form, Formik, type FormikErrors, type FormikTouched } from "formik";
import { useMemo } from "react";
import { Alert } from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import { Input } from "@/core/components/ui/input";
import { SimpleSidebar } from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { RosterFieldControl } from "@/payroll/roster/roster-field-control";
import {
  type PayrollRosterContract,
  type PayrollRosterField,
  type PayrollRosterMember,
  type PayrollRosterFormValues,
  type PayrollTeam,
} from "@/payroll/roster/types";
import {
  type PayrollRosterMemberErrors,
  validatePayrollRosterMember,
  toPayrollRosterFormValues,
  toPayrollRosterMember,
} from "@/payroll/roster/utils";
import { DrawerField } from "./drawer-field";
import { RosterEditDrawerFooter } from "./roster-edit-drawer-footer";
import { RosterMemberAccessFields } from "./roster-member-access-fields";

const toFormikErrors = (
  errors: PayrollRosterMemberErrors,
): FormikErrors<PayrollRosterMember> => ({
  ...(errors.display_name ? { display_name: errors.display_name } : {}),
  ...(errors.service_titan_employee_id
    ? { service_titan_employee_id: errors.service_titan_employee_id }
    : {}),
  ...(Object.keys(errors.field_values).length > 0
    ? { field_values: errors.field_values }
    : {}),
});

interface RosterEditDrawerProps {
  businessId: string;
  teams: PayrollTeam[];
  title: string;
  fields: PayrollRosterField[];
  // fixed_option choices per field key, sourced live from the contract.
  fieldOptions: Record<string, string[]>;
  contract: PayrollRosterContract;
  initialMember: PayrollRosterMember;
  canRemove: boolean;
  // Suggestions only: say this person does not belong on payroll at all.
  onDismiss?: () => void;
  showInitialErrors: boolean;
  saving: boolean;
  dismissing: boolean;
  onSave: (member: PayrollRosterMember) => Promise<void>;
  onCancel: () => void;
  onRemove: () => void;
}

// The right-side edit surface: one tech at a time. Edits accumulate locally and
// only reach the draft on Save; Cancel throws them away. Nothing publishes here.
export const RosterEditDrawer = ({
  businessId,
  teams,
  title,
  fields,
  fieldOptions,
  contract,
  initialMember,
  canRemove,
  onDismiss,
  showInitialErrors,
  saving,
  dismissing,
  onSave,
  onCancel,
  onRemove,
}: RosterEditDrawerProps) => {
  const initialValues = useMemo(
    () => toPayrollRosterFormValues(initialMember),
    [initialMember],
  );

  // Errors the person already had when the drawer opened. For a FLAGGED person
  // (a contract change invalidated a saved value, or made a blank field newly
  // required) these are non-empty; for a valid tech / new person they're empty.
  // Stable for the drawer's lifetime (it remounts per person via `key`).
  const initialErrors = useMemo(
    () => validatePayrollRosterMember(initialMember, fields, contract),
    [initialMember, fields, contract],
  );

  // Seed Formik's touched state with every field already in error at open, so a
  // pre-existing problem (a removed option, a newly-required blank) shows its
  // red border + inline message immediately — no interaction needed. Fields
  // that are valid at open stay un-nagged until the user touches them.
  const initialTouched = useMemo<FormikTouched<PayrollRosterMember>>(
    () =>
      showInitialErrors
        ? {
            display_name: Boolean(initialErrors.display_name),
            service_titan_employee_id: Boolean(
              initialErrors.service_titan_employee_id,
            ),
            field_values: Object.fromEntries(
              Object.keys(initialErrors.field_values).map((key) => [key, true]),
            ),
          }
        : {},
    [initialErrors, showInitialErrors],
  );

  return (
    <SimpleSidebar aria-label={title}>
      <Stack
        direction="row"
        align="center"
        justify="between"
        fullWidth
        className="border-b border-stroke-tertiary p-6"
      >
        <Stack gap="none">
          <Typography variant="subtitleW1">{title}</Typography>
          <Typography variant="bodyW3" textColor="secondary">
            Update all fields, then save the roster once.
          </Typography>
        </Stack>
        <Button
          size="icon"
          variant="text"
          aria-label="Close editor"
          disabled={saving}
          onClick={onCancel}
        >
          <XMarkIcon />
        </Button>
      </Stack>

      <Formik<PayrollRosterFormValues>
        initialValues={initialValues}
        initialTouched={initialTouched}
        validate={(member) =>
          toFormikErrors(validatePayrollRosterMember(member, fields, contract))
        }
        onSubmit={(values) => onSave(toPayrollRosterMember(values, fields))}
        validateOnMount
        enableReinitialize
      >
        {({
          errors,
          touched,
          getFieldProps,
          isSubmitting,
          submitCount,
          setFieldTouched,
          setFieldValue,
          values,
        }) => {
          const busy = saving || isSubmitting;
          const nameError = touched.display_name
            ? errors.display_name
            : undefined;
          const employeeIdError = touched.service_titan_employee_id
            ? errors.service_titan_employee_id
            : undefined;
          const attentionFields = fields.filter(
            (field) =>
              showInitialErrors &&
              field.key in initialErrors.field_values &&
              field.key in (errors.field_values ?? {}),
          );

          return (
            <Form className="flex min-h-0 flex-1 flex-col">
              <Stack gap="none" className="flex-1 overflow-y-auto p-6">
                <Stack gap="lg" fullWidth>
                  {attentionFields.length > 0 && (
                    <Alert variant="destructive">
                      <Stack gap="xs">
                        <Typography variant="subtitleW3">
                          Needs attention
                        </Typography>
                        <Typography variant="bodyW3" textColor="secondary">
                          {attentionFields
                            .map((field) => field.label)
                            .join(", ")}{" "}
                          {attentionFields.length === 1 ? "is" : "are"} no
                          longer valid — pick{" "}
                          {attentionFields.length === 1
                            ? "a new value"
                            : "new values"}
                          .
                        </Typography>
                      </Stack>
                    </Alert>
                  )}

                  <RosterMemberAccessFields
                    businessId={businessId}
                    teams={teams}
                    saving={busy}
                  />
                  <DrawerField label="Name" required error={nameError}>
                    <Input
                      fullWidth
                      aria-label="Name"
                      error={nameError}
                      disabled={busy}
                      {...getFieldProps("display_name")}
                    />
                  </DrawerField>

                  <DrawerField
                    label="ServiceTitan employee ID"
                    required
                    error={employeeIdError}
                  >
                    <Input
                      fullWidth
                      aria-label="ServiceTitan employee ID"
                      error={employeeIdError}
                      disabled={busy}
                      {...getFieldProps("service_titan_employee_id")}
                    />
                  </DrawerField>

                  {fields.map((field) => {
                    const fieldError =
                      submitCount > 0 || touched.field_values?.[field.key]
                        ? errors.field_values?.[field.key]
                        : undefined;

                    return (
                      <DrawerField
                        key={field.key}
                        label={field.label}
                        required={field.required}
                        error={fieldError}
                      >
                        <RosterFieldControl
                          field={field}
                          options={fieldOptions[field.key] ?? []}
                          value={values.field_values[field.key]}
                          error={fieldError}
                          disabled={busy}
                          onChange={(value) => {
                            void setFieldValue(
                              `field_values.${field.key}`,
                              value,
                            );
                            void setFieldTouched(
                              `field_values.${field.key}`,
                              true,
                              false,
                            );
                          }}
                        />
                      </DrawerField>
                    );
                  })}
                </Stack>
              </Stack>

              <RosterEditDrawerFooter
                canRemove={canRemove}
                onDismiss={onDismiss}
                saving={busy}
                dismissing={dismissing}
                onCancel={onCancel}
                onRemove={onRemove}
              />
            </Form>
          );
        }}
      </Formik>
    </SimpleSidebar>
  );
};
